/**
 * Presentation View — full-screen, theatrical display for second monitor.
 * Connects to Socket.io and renders slides based on host commands.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { QRCodeSVG } from 'qrcode.react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import api from '../../utils/api';
import { drawingBlockedReason } from '../../utils/drawing';
import config from '../../config';
import { useSiteConfig } from '../../utils/siteConfig';
import { PRODUCT_BRAND } from '../../utils/productBrand';
import type { CustomSlide } from '../../utils/slideSequence';
import { choiceColumns, choiceLetter } from '../../utils/multipleChoice';
import './PresentationView.css';

// ──────────────────────────────────────────────────────────────────────
// FullscreenButton — toggles native browser fullscreen on the document
// element. Hidden while in fullscreen so it doesn't appear on the
// projector. ESC exits fullscreen as normal.
// ──────────────────────────────────────────────────────────────────────
function FullscreenButton() {
  const [isFullscreen, setIsFullscreen] = useState(
    typeof document !== 'undefined' && Boolean(document.fullscreenElement)
  );

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggle = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      // Some browsers reject programmatic fullscreen if not in a user
      // gesture; the click handler should always be one, but if for some
      // reason it errors we just no-op rather than logging visibly.
    }
  }, []);

  // Cmd+Enter (Mac) / Ctrl+Enter (Win/Linux) toggles fullscreen. The
  // keydown itself is a user gesture so requestFullscreen is allowed.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  if (isFullscreen) return null;
  return (
    <button
      type="button"
      className="presentation-fullscreen-btn"
      onClick={toggle}
      title="Fullscreen (⌘+Enter / Ctrl+Enter)"
      aria-label="Enter fullscreen"
    >
      ⛶
    </button>
  );
}

interface Question {
  id: string;
  orderIndex: number;
  text: string;
  // answer fields are intentionally omitted — the by-pin endpoint strips them for security
}

interface Round {
  id: string;
  orderIndex: number;
  title: string;
  // roundType is a constraint on question types; song data lives on each
  // SONG-type question now, not on the round.
  roundType: 'STANDARD' | 'VISUAL' | 'AUDIO';
  questions: Question[];
}

interface EventData {
  id: string;
  pin: string;
  status: string;
  // QR team check-in is on for this night: show the join code on the
  // pre-game slides.
  checkInEnabled?: boolean;
  // Optional display-name override set in the live-events admin.
  // Falls back to quiz.name if null.
  name?: string | null;
  venue: { name: string };
  // Resolved welcome/rules slide content from the backend. Most specific
  // source wins: liveEvent override -> venue default -> quiz legacy value.
  // Optional so older payloads (without resolution) still fall back to the
  // raw quiz.welcome* fields below.
  welcome?: {
    prizes: string | null;
    rulesText: string | null;
    startTime: string | null;
  } | null;
  quiz: {
    name: string;
    // Welcome / rules content. NULL/empty values hide the corresponding
    // line on the welcome slide or suppress the rules slide.
    welcomePrizes: string | null;
    welcomeStartTime: string | null;
    rulesText: string | null;
    // How many top ranks get the gold/silver/bronze styling. 0 = no
    // highlights (all rows render plain). Defaults to 3 if missing.
    standingsHighlightCount?: number | null;
    rounds: Round[];
  };
  teams: Array<{
    id: string;
    name: string;
  }>;
  // Host-made slides for this night. The server sends only what the host
  // typed on each slide, never question data.
  customSlides?: CustomSlide[];
}

interface StandingEntry {
  teamName: string;
  grandTotal: number;
  rank: number;
}

type SlideKind =
  | 'WELCOME'
  | 'RULES'
  | 'ROUNDS_OVERVIEW'
  | 'ROUND_INTRO'
  | 'QUESTION'
  | 'ANSWER'
  | 'ROUND_SUMMARY'
  | 'STANDINGS'
  | 'VISUAL_ROUND'
  | 'SONG_BONUS'
  | 'DRAWING'
  | 'OUTRO'
  | 'CUSTOM';

type DrawingPhase = 'IDLE' | 'ROLLING' | 'REVEALED';

interface UpcomingItem {
  id: string;
  name: string;
  venueName: string;
  theme: string | null;
  date: Date;
}

// URLs surfaced on the OUTRO slide as QR codes, per org via siteConfig
// (Settings → Branding). The server sends an empty string for an unset
// link, and an empty link hides its QR. The fallbacks are empty too, so
// one org never shows another org's links.
// `handle` is the human-friendly name shown under the QR.
export function useOutroLinks() {
  const { get } = useSiteConfig();
  const siteUrl = get('publicSiteUrl');
  const instaUrl = get('socialInstagramUrl');
  const instaHandle = get('socialInstagramHandle');
  const links = [];
  if (siteUrl) {
    links.push({ label: 'Website', handle: siteUrl.replace(/^https?:\/\/(www\.)?/, ''), icon: faLink, url: siteUrl });
  }
  if (instaUrl) {
    links.push({ label: 'Instagram', handle: instaHandle, icon: faInstagram, url: instaUrl });
  }
  return links;
}

// Format a seconds value as "M:SS" for the audio progress display.
function formatClock(secs: number): string {
  if (!Number.isFinite(secs) || secs < 0) return '0:00';
  const total = Math.floor(secs);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Split a free-text prize line into list items. Tolerant of multiple
// separators so old data like "$20 1st · $15 2nd" still renders as a
// proper list without needing the host to re-enter every quiz.
// Newlines, middle-dot, and pipe are all treated as item boundaries;
// commas are intentionally NOT separators because they're commonly
// used inside individual prizes ("$20 cash, $15 store credit").
function splitPrizes(raw: string): string[] {
  return raw
    .split(/[\r\n]+|\s+·\s+|\s+\|\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// Construct a Date that represents `hour:minute` on (year, month0, day)
// as wall-clock time in the given IANA timezone. Works regardless of
// the browser's local TZ — what matters is "Wed 7pm MT", not "Wed 7pm
// wherever this device happens to be set." Iterative correction handles
// DST boundaries cleanly in at most two passes.
function dateInTimeZone(
  year: number,
  month0: number,
  day: number,
  hour: number,
  minute: number,
  tz: string,
): Date {
  // First guess: pretend the target wall-clock time is UTC.
  let guess = new Date(Date.UTC(year, month0, day, hour, minute));
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });
  for (let i = 0; i < 2; i++) {
    const parts = fmt.formatToParts(guess);
    const get = (k: string) => Number(parts.find((p) => p.type === k)?.value ?? 0);
    const gotHour = get('hour') === 24 ? 0 : get('hour');
    const targetUtc = Date.UTC(year, month0, day, hour, minute);
    const gotUtc = Date.UTC(get('year'), get('month') - 1, get('day'), gotHour, get('minute'));
    const diff = targetUtc - gotUtc;
    if (diff === 0) break;
    guess = new Date(guess.getTime() + diff);
  }
  return guess;
}

// Expand a single recurring event into its next N weekly occurrences,
// anchored to America/Denver wall-clock time. Browser local TZ is
// irrelevant — Wednesday 7pm at West Slope Quiz is Wednesday 7pm Mountain,
// even if the presentation laptop's clock is set to UTC.
function expandRecurring(ev: { name: string; recurringDay: string | null; recurringTime: string | null; recurringShowWeeks: number | null }): Date[] {
  const TZ = 'America/Denver';
  const now = new Date();
  const days = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
  let day = ev.recurringDay;
  if (!day) day = days.find((d) => ev.name.toLowerCase().includes(d)) ?? null;
  if (!day) return [];
  const eventDay = days.indexOf(day.toLowerCase());

  // Today's Y/M/D and weekday AS SEEN IN MT — not in browser local.
  // weekday: 'long' returns "Wednesday" so it lines up with the full
  // day names in `days`. Using 'short' here returned "Wed" and the
  // indexOf fell through to -1, which made every recurring quiz show
  // as four days out (Sunday for a Wednesday-today expansion).
  const nowParts = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ, year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'long',
  }).formatToParts(now);
  const todayYear = Number(nowParts.find((p) => p.type === 'year')!.value);
  const todayMonth0 = Number(nowParts.find((p) => p.type === 'month')!.value) - 1;
  const todayDay = Number(nowParts.find((p) => p.type === 'day')!.value);
  const todayWeekday = days.indexOf(
    (nowParts.find((p) => p.type === 'weekday')!.value || '').toLowerCase(),
  );
  // Resolve "today + N days" arithmetic via UTC noon so DST changes
  // don't shift the calendar day.
  const todayNoonUtc = new Date(Date.UTC(todayYear, todayMonth0, todayDay, 12, 0));

  const [hStr, mStr] = (ev.recurringTime ?? '19:30').split(':');
  const hour = Number.isFinite(Number(hStr)) ? Number(hStr) : 19;
  const minute = Number.isFinite(Number(mStr)) ? Number(mStr) : 30;

  let diff = eventDay - todayWeekday;
  if (diff < 0) diff += 7;
  const firstNoon = new Date(todayNoonUtc.getTime() + diff * 24 * 60 * 60 * 1000);
  const firstParts = {
    y: firstNoon.getUTCFullYear(),
    m: firstNoon.getUTCMonth(),
    d: firstNoon.getUTCDate(),
  };
  let first = dateInTimeZone(firstParts.y, firstParts.m, firstParts.d, hour, minute, TZ);
  if (first <= now) {
    const nextNoon = new Date(firstNoon.getTime() + 7 * 24 * 60 * 60 * 1000);
    first = dateInTimeZone(nextNoon.getUTCFullYear(), nextNoon.getUTCMonth(), nextNoon.getUTCDate(), hour, minute, TZ);
  }

  const out: Date[] = [first];
  const weeks = ev.recurringShowWeeks ?? 1;
  // Step forward in 7-day jumps anchored to MT noon so DST transitions
  // don't shift the weekday or accidentally double-count a date.
  let cursorNoon = new Date(
    Date.UTC(
      first.getUTCFullYear(),
      first.getUTCMonth(),
      first.getUTCDate(),
      12,
      0,
    ),
  );
  // Re-derive the MT calendar date of `first` (the UTC date might be
  // the previous day if first is after midnight UTC but before midnight
  // MT — common for evening events).
  const firstMtParts = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ, year: 'numeric', month: 'numeric', day: 'numeric',
  }).formatToParts(first);
  cursorNoon = new Date(
    Date.UTC(
      Number(firstMtParts.find((p) => p.type === 'year')!.value),
      Number(firstMtParts.find((p) => p.type === 'month')!.value) - 1,
      Number(firstMtParts.find((p) => p.type === 'day')!.value),
      12,
      0,
    ),
  );
  for (let i = 1; i < weeks; i++) {
    cursorNoon = new Date(cursorNoon.getTime() + 7 * 24 * 60 * 60 * 1000);
    out.push(
      dateInTimeZone(
        cursorNoon.getUTCFullYear(),
        cursorNoon.getUTCMonth(),
        cursorNoon.getUTCDate(),
        hour,
        minute,
        TZ,
      ),
    );
  }
  return out;
}

interface SummaryQuestion {
  orderIndex: number;
  text: string;
  answer?: string;
  answerPartB?: string | null;
  // Multiple choice: the choice texts. correctChoice arrives only with the
  // answers (the server strips it from every other slide).
  choices?: string[];
  correctChoice?: number | null;
}

// Read a choice list from a socket payload. Anything that is not a list of
// 2 or more strings means "not multiple choice".
function readChoices(raw: unknown): string[] {
  if (!Array.isArray(raw) || raw.length < 2) return [];
  return raw.slice(0, 6).map((c) => (typeof c === 'string' ? c : ''));
}

function readIndex(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 ? raw : null;
}

// The choices of one question on the round summary, as one line of letter
// chips. With the answers shown, the correct chip lights up and the others
// dim.
function SummaryChoices({ choices, correct }: { choices: string[]; correct: number | null }) {
  const revealed = correct != null;
  return (
    <span className={`summary-choices ${revealed ? 'revealed' : ''}`}>
      {choices.map((c, i) => (
        <span
          key={i}
          className={`summary-choice ${revealed ? (i === correct ? 'correct' : 'dimmed') : ''}`}
        >
          <span className="summary-choice-letter">{choiceLetter(i)}</span>
          {c}
        </span>
      ))}
    </span>
  );
}

// The choices grid on the question and answer slides. Before the reveal
// (correct = null) every choice looks the same. At the reveal the correct
// choice lights up and the others dim.
function ChoiceGrid({ choices, correct }: { choices: string[]; correct: number | null }) {
  const revealed = correct != null;
  return (
    <ol className={`mc-choices ${revealed ? 'revealed' : ''}`} data-cols={choiceColumns(choices)}>
      {choices.map((c, i) => (
        <li
          key={i}
          className={`mc-choice ${revealed ? (i === correct ? 'correct' : 'dimmed') : ''}`}
          style={revealed ? undefined : { animationDelay: `${0.15 + i * 0.08}s` }}
        >
          <span className="mc-letter">{choiceLetter(i)}</span>
          <span className="mc-text">{c}</span>
        </li>
      ))}
    </ol>
  );
}

interface SlideState {
  kind: SlideKind;
  roundNumber: number;
  roundTitle: string;
  roundType: string;
  questionNumber: number;
  questionText: string;
  answer: string;
  answerPartB: string;
  // Multiple choice question: the choice texts, and (only on the ANSWER
  // slide) the correct index.
  choices: string[];
  correctChoice: number | null;
  // The question's own point value when it differs from the round value.
  // null = no tag.
  points: number | null;
  // A song slide never holds the title or the artist: the projector must
  // not know the answer before the reveal. It gets only the clip.
  songAudioPath: string;
  // Clip length in seconds from the quiz, used until the audio file
  // reports its own duration. 0 = unknown.
  songDuration: number;
  // Round-summary fields. Only populated when kind === 'ROUND_SUMMARY'.
  summaryQuestions: SummaryQuestion[];
  summaryAnswersShown: boolean;
  summaryVisibleCount: number;
  // How many answers to actually display. The host reveals answers one at
  // a time, so this climbs from 0 to the question count independently of
  // summaryVisibleCount. Answers for all questions are present in the
  // payload once answers are shown (so the auto-sizer stays stable); this
  // gates which ones render.
  summaryAnswerVisibleCount: number;
  // 1.0 = auto-sized (default). Host can nudge up/down to fit content.
  summaryScale: number;
  totalQuestions: number;
  totalRounds: number;
  eventId: string;
  isWelcome: boolean;
  // For CUSTOM: the placement id of the slide to show.
  customSlideId: string;
}

const EMPTY_SLIDE: SlideState = {
  kind: 'WELCOME',
  roundNumber: 0,
  roundTitle: '',
  roundType: '',
  questionNumber: 0,
  questionText: '',
  answer: '',
  answerPartB: '',
  choices: [],
  correctChoice: null,
  points: null,
  songAudioPath: '',
  songDuration: 0,
  summaryQuestions: [],
  summaryAnswersShown: false,
  summaryVisibleCount: 0,
  summaryAnswerVisibleCount: 0,
  summaryScale: 1,
  totalQuestions: 0,
  totalRounds: 0,
  eventId: '',
  isWelcome: true,
  customSlideId: '',
};

const PresentationView: React.FC = () => {
  const { pin } = useParams<{ pin: string }>();
  const outroLinks = useOutroLinks();
  // The server decides this from the event org's plan and settings.
  const showPoweredBy = useSiteConfig().meta?.showPoweredBy ?? false;
  const [event, setEvent] = useState<EventData | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [slide, setSlide] = useState<SlideState>(EMPTY_SLIDE);
  const [standings, setStandings] = useState<StandingEntry[]>([]);
  const [connected, setConnected] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState<number | null>(null);
  // Reminder overlay text — set by the host via SHOW_REMINDER, cleared by
  // CLEAR_REMINDER or by sending an empty string. Rendered as a banner on
  // top of whatever slide is currently showing.
  const [reminderText, setReminderText] = useState('');
  // Random-drawing state — drives the slot-machine animation on the
  // DRAWING slide. IDLE shows the Draw button; ROLLING cycles names;
  // REVEALED shows the winner. Pool is the eligible-team names used
  // for the cycle.
  const [drawPhase, setDrawPhase] = useState<DrawingPhase>('IDLE');
  const [drawDisplayName, setDrawDisplayName] = useState('');
  const [drawWinner, setDrawWinner] = useState('');
  const [drawError, setDrawError] = useState('');
  const drawTimersRef = useRef<number[]>([]);
  // Upcoming events for the OUTRO slide. Loaded lazily the first time
  // the slide is shown so we don't hit the API for every event.
  const [upcoming, setUpcoming] = useState<UpcomingItem[]>([]);
  const upcomingLoadedRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const eventRef = useRef<EventData | null>(null);
  // The custom slide last drawn, for a slide removed while on screen.
  const lastCustomRef = useRef<CustomSlide | null>(null);

  // Keep ref in sync so socket handler always has current event
  useEffect(() => {
    eventRef.current = event;
  }, [event]);

  // Load event data by PIN
  useEffect(() => {
    if (!pin) return;
    const load = async () => {
      try {
        const res = await api.get(`/api/events/by-pin/${pin}`);
        setEvent(res.data.data.event);
      } catch (err) {
        console.error('Failed to load event by PIN', err);
        setLoadError(true);
      }
    };
    load();
  }, [pin]);

  // Fetch standings when requested. throughRound (1-based) narrows the
  // calculation to rounds <= N so navigating back to "Round 5
  // Standings" mid-Round-7 shows what the leaderboard looked like at
  // the end of R5, not the current totals.
  const fetchStandings = async (eventId: string, throughRound?: number) => {
    try {
      const qs = throughRound ? `?throughRound=${throughRound}` : '';
      const res = await api.get(`/api/scores/event/${eventId}/standings${qs}`);
      // Show every active team — the CSS auto-scales row size by team
      // count via data-density so >10 teams still fits the viewport.
      setStandings(res.data.data.standings);
    } catch (err) {
      console.error('Failed to fetch standings', err);
    }
  };
  const [standingsRound, setStandingsRound] = useState<number | null>(null);

  // Connect to Socket.io — depends only on pin (stable)
  useEffect(() => {
    if (!pin) return;

    // Socket.io interprets a path component on the URL as a *namespace*.
    // Our config.apiBaseUrl is e.g. 'https://westslopequiz.com/api' in prod,
    // which would put us on namespace '/api'. The server only listens on
    // the default namespace, so we connect to the origin instead.
    const socketOrigin = (() => {
      try { return new URL(config.apiBaseUrl).origin; }
      catch { return config.apiBaseUrl; }
    })();
    const socket: Socket = io(socketOrigin, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      // (Re-)join the event room on every connect/reconnect. The server
      // will replay the cached current slide so the audience doesn't
      // sit on the welcome slide until the host clicks Next.
      socket.emit('join-event', { pin });
    });

    // Bridge for the audio element's onError. The audio tag lives in
    // the slide JSX and doesn't have direct access to the socket, so it
    // dispatches a window event and we forward it here.
    const onAudioErr = (e: Event) => {
      const url = (e as CustomEvent).detail?.url;
      socket.emit('audio-error', { pin, url });
    };
    window.addEventListener('present-audio-error', onAudioErr);

    socket.on('disconnect', () => {
      setConnected(false);
    });

    // The server pushes a new custom-slide list after an operator changes
    // this night's slides. It is the same projector-safe list as /by-pin.
    socket.on('slides-updated', ({ customSlides }: { customSlides?: CustomSlide[] }) => {
      if (!Array.isArray(customSlides)) return;
      setEvent((prev) => (prev ? { ...prev, customSlides } : prev));
    });

    // The host switched QR check-in on or off in the hub.
    socket.on('checkin-changed', ({ enabled }: { enabled: boolean }) => {
      setEvent((prev) => (prev ? { ...prev, checkInEnabled: Boolean(enabled) } : prev));
    });

    socket.on('presentation-update', ({ action, payload }: { action: string; payload: Record<string, unknown> }) => {
      setAnimKey((k) => k + 1);

      switch (action) {
        case 'SHOW_WELCOME':
          setSlide({ ...EMPTY_SLIDE, kind: 'WELCOME' });
          break;

        case 'SHOW_RULES':
          setSlide({ ...EMPTY_SLIDE, kind: 'RULES', isWelcome: false });
          break;

        case 'SHOW_ROUNDS_OVERVIEW':
          setSlide({ ...EMPTY_SLIDE, kind: 'ROUNDS_OVERVIEW', isWelcome: false });
          break;

        case 'SHOW_QUESTION':
          setSlide({
            ...EMPTY_SLIDE,
            kind: 'QUESTION',
            roundNumber: (payload.roundNumber as number) || 0,
            roundTitle: (payload.roundTitle as string) || '',
            roundType: (payload.roundType as string) || '',
            questionNumber: (payload.questionNumber as number) || 0,
            questionText: (payload.questionText as string) || '',
            totalQuestions: (payload.totalQuestions as number) || 0,
            totalRounds: (payload.totalRounds as number) || 0,
            choices: readChoices(payload.choices),
            points: typeof payload.points === 'number' ? (payload.points as number) : null,
            isWelcome: false,
          });
          break;

        case 'SHOW_ANSWER':
          setSlide((prev) => ({
            ...prev,
            kind: 'ANSWER',
            answer: (payload.answer as string) || '',
            answerPartB: (payload.answerPartB as string) || '',
            choices: Array.isArray(payload.choices) ? readChoices(payload.choices) : prev.choices,
            correctChoice: readIndex(payload.correctChoice),
            questionText: (payload.questionText as string) || prev.questionText,
            roundNumber: (payload.roundNumber as number) || prev.roundNumber,
            questionNumber: (payload.questionNumber as number) || prev.questionNumber,
          }));
          break;

        case 'SHOW_ROUND_INTRO':
          setSlide({
            ...EMPTY_SLIDE,
            kind: 'ROUND_INTRO',
            roundNumber: (payload.roundNumber as number) || 0,
            roundTitle: (payload.roundTitle as string) || '',
            roundType: (payload.roundType as string) || '',
            totalRounds: (payload.totalRounds as number) || 0,
            isWelcome: false,
          });
          break;

        case 'SHOW_STANDINGS': {
          setSlide({ ...EMPTY_SLIDE, kind: 'STANDINGS', isWelcome: false });
          const throughRound = typeof payload.throughRound === 'number'
            ? (payload.throughRound as number)
            : null;
          setStandingsRound(throughRound);
          // Use ref to avoid stale closure; fall back to payload
          if (eventRef.current?.id) {
            fetchStandings(eventRef.current.id, throughRound ?? undefined);
          } else if (payload.eventId) {
            fetchStandings(payload.eventId as string, throughRound ?? undefined);
          }
        }
          break;

        case 'SHOW_VISUAL_ROUND':
          setSlide({
            ...EMPTY_SLIDE,
            kind: 'VISUAL_ROUND',
            roundNumber: (payload.roundNumber as number) || 0,
            roundTitle: (payload.roundTitle as string) || '',
            isWelcome: false,
          });
          break;

        case 'SHOW_ROUND_SUMMARY': {
          const roundType = (payload.roundType as string) || '';
          let qs: SummaryQuestion[] = Array.isArray(payload.questions)
            ? (payload.questions as SummaryQuestion[])
            : [];
          // For AUDIO rounds, the per-question text is "Clip 1", "Clip 2", …
          // — meaningless on the recap, where the answer already names the
          // song. Strip the text so the audience just sees a numbered list
          // of "Song — Artist" answers.
          if (roundType === 'AUDIO') {
            qs = qs.map((q) => ({ ...q, text: '' }));
          }
          // visibleCount controls how many of the questions are rendered.
          // Defaults to ALL when the host didn't send a count (e.g. older
          // clients) so we don't silently hide content.
          const visible =
            typeof payload.visibleCount === 'number'
              ? payload.visibleCount
              : qs.length;
          // How many answers to show. Older hosts didn't send this; if
          // they flagged answersShown we reveal all answers at once, which
          // preserves the previous all-at-once behaviour.
          const answersShown = Boolean(payload.answersShown);
          const answerVisible =
            typeof payload.answerVisibleCount === 'number'
              ? payload.answerVisibleCount
              : answersShown
                ? qs.length
                : 0;
          setSlide({
            ...EMPTY_SLIDE,
            kind: 'ROUND_SUMMARY',
            roundNumber: (payload.roundNumber as number) || 0,
            roundTitle: (payload.roundTitle as string) || '',
            roundType,
            totalRounds: (payload.totalRounds as number) || 0,
            summaryQuestions: qs,
            summaryAnswersShown: answersShown,
            summaryVisibleCount: visible,
            summaryAnswerVisibleCount: answerVisible,
            summaryScale:
              typeof payload.scale === 'number' && payload.scale > 0
                ? (payload.scale as number)
                : 1,
            isWelcome: false,
          });
          break;
        }

        case 'SHOW_SONG':
          // Navigate to the song slide WITHOUT starting playback.
          // The audience sees the prompt + audio shell; the host
          // hits Play Song separately to start the clip.
          setSlide({
            ...EMPTY_SLIDE,
            kind: 'SONG_BONUS',
            roundNumber: (payload.roundNumber as number) || 0,
            roundTitle: (payload.roundTitle as string) || '',
            questionNumber: (payload.questionNumber as number) || 0,
            totalQuestions: (payload.totalQuestions as number) || 0,
            songAudioPath: (payload.songAudioPath as string) || '',
            songDuration: Number(payload.songDuration) || 0,
            isWelcome: false,
          });
          setAudioBlocked(false);
          break;

        case 'PLAY_SONG':
          // Explicit "start playing now" — host clicked Play Song.
          // Update the slide too in case the host hadn't navigated
          // here yet (or the audience reconnected and was on stale
          // state).
          setSlide((prev) => ({
            ...EMPTY_SLIDE,
            kind: 'SONG_BONUS',
            roundNumber: (payload.roundNumber as number) || prev.roundNumber,
            roundTitle: (payload.roundTitle as string) || prev.roundTitle,
            questionNumber: (payload.questionNumber as number) || prev.questionNumber,
            totalQuestions: (payload.totalQuestions as number) || prev.totalQuestions,
            songAudioPath: (payload.songAudioPath as string) || prev.songAudioPath,
            songDuration: Number(payload.songDuration) || prev.songDuration,
            isWelcome: false,
          }));
          // Defer one tick so the audio element with the new src is
          // mounted before we call play() on it.
          window.setTimeout(() => {
            const a = audioRef.current;
            if (!a) return;
            // Always rewind so successive Play Song clicks start at
            // the configured clip start, not where the previous play
            // got cut off.
            try { a.currentTime = 0; } catch { /* some browsers throw before metadata */ }
            a.play().then(() => setAudioBlocked(false)).catch((err) => {
              if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
                setAudioBlocked(true);
              }
            });
          }, 50);
          break;

        case 'PAUSE_SONG':
          // Pause whatever's playing without disturbing the slide.
          // Safe to call on an already-paused element.
          audioRef.current?.pause();
          break;

        case 'RESUME_SONG':
          // Resume from current position. The play() promise can reject
          // if the user hasn't interacted with the page yet, in which
          // case the existing <audio controls> on screen lets the host
          // click play manually.
          audioRef.current?.play().catch(() => {});
          break;

        case 'RESTART_SONG':
          // Jump to the start and resume. Useful for "wait one more
          // play-through" calls late in the round.
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => {});
          }
          break;

        case 'TIMER':
          setTimerSeconds(
            (payload.seconds as number) > 0
              ? (payload.seconds as number)
              : null
          );
          break;

        case 'SHOW_REMINDER':
          setReminderText(typeof payload.text === 'string' ? payload.text : '');
          break;

        case 'CLEAR_REMINDER':
          setReminderText('');
          break;

        case 'SHOW_OUTRO':
          setSlide({ ...EMPTY_SLIDE, kind: 'OUTRO', isWelcome: false });
          if (!upcomingLoadedRef.current) {
            upcomingLoadedRef.current = true;
            // Fetch the public upcoming-events feed and expand recurring
            // entries client-side, same as the /schedule page does.
            fetch(`${config.apiBaseUrl}/events/upcoming`)
              .then((r) => r.ok ? r.json() : [])
              .then((rows: Array<{ id: string; name: string; date?: string | null; type?: string; theme?: string | null; venue?: { name: string }; recurringDay: string | null; recurringTime: string | null; recurringShowWeeks: number | null }>) => {
                const items: UpcomingItem[] = [];
                for (const ev of rows) {
                  if (!ev.venue) continue;
                  const theme = ev.theme ?? null;
                  if (ev.type === 'recurring' && !ev.date) {
                    for (const d of expandRecurring(ev)) {
                      items.push({ id: `${ev.id}-${d.getTime()}`, name: ev.name, venueName: ev.venue.name, theme, date: d });
                    }
                  } else if (ev.date) {
                    items.push({ id: `${ev.id}`, name: ev.name, venueName: ev.venue.name, theme, date: new Date(ev.date) });
                  }
                }
                items.sort((a, b) => a.date.getTime() - b.date.getTime());
                setUpcoming(items.slice(0, 8));
              })
              .catch(() => {
                // Best-effort. If the API is down the outro just shows
                // the QR codes without the schedule list.
                setUpcoming([]);
              });
          }
          break;

        case 'SHOW_CUSTOM': {
          // A host-made slide. The payload carries only the placement id.
          // The content comes from this screen's own /by-pin copy, so the
          // socket cannot put anything else on the projector.
          const slideId = typeof payload.slideId === 'string' ? payload.slideId : '';
          setSlide({ ...EMPTY_SLIDE, kind: 'CUSTOM', customSlideId: slideId, isWelcome: false });
          // The host may have placed this slide after the screen loaded.
          // Reload the night once so the slide can show.
          const known = eventRef.current?.customSlides?.some((c) => c.id === slideId);
          if (slideId && !known) {
            api.get(`/api/events/by-pin/${pin}`)
              .then((res) => setEvent(res.data.data.event))
              .catch(() => { /* keep the current copy */ });
          }
          break;
        }

        case 'SHOW_DRAWING':
          // Idle drawing slide — host navigated to the draw slot. Reset
          // state so the audience sees the Draw button, not stale winner.
          setSlide({ ...EMPTY_SLIDE, kind: 'DRAWING', isWelcome: false });
          setDrawPhase('IDLE');
          setDrawWinner('');
          setDrawDisplayName('');
          setDrawError('');
          break;

        case 'PLAY_DRAWING': {
          // One client picked the winner + the eligible pool and
          // broadcast it. Every audience tab runs the same animation
          // off the same data, so projector + side displays stay in
          // sync. Clear any prior timers first in case Draw was hit
          // twice rapidly.
          for (const t of drawTimersRef.current) { window.clearTimeout(t); }
          drawTimersRef.current = [];
          const winner = (payload.winnerName as string) || '';
          const pool = Array.isArray(payload.pool) ? (payload.pool as string[]) : [];
          if (!winner || pool.length === 0) break;
          setSlide((prev) => prev.kind === 'DRAWING' ? prev : ({ ...EMPTY_SLIDE, kind: 'DRAWING', isWelcome: false }));
          setDrawWinner(winner);
          setDrawPhase('ROLLING');
          // Slot-machine schedule: tick names with progressively longer
          // gaps so the cycle slows visibly into the winner reveal.
          // Total duration ≈ 3.6s.
          const intervals = [
            70,70,70,70,70,70,70,70,70,70,70,70,    // ~1s fast
            85,100,120,140,170,200,240,290,         // slowdown
            350,420,500,600,                        // long pauses
          ];
          let elapsed = 0;
          intervals.forEach((gap, i) => {
            elapsed += gap;
            const id = window.setTimeout(() => {
              // Land on the winner for the final two ticks so the
              // last name shown matches the reveal.
              const name = i >= intervals.length - 2
                ? winner
                : pool[Math.floor(Math.random() * pool.length)] ?? winner;
              setDrawDisplayName(name);
            }, elapsed);
            drawTimersRef.current.push(id);
          });
          const revealId = window.setTimeout(() => {
            setDrawDisplayName(winner);
            setDrawPhase('REVEALED');
          }, elapsed + 200);
          drawTimersRef.current.push(revealId);
          break;
        }
      }
    });

    return () => {
      window.removeEventListener('present-audio-error', onAudioErr);
      socket.disconnect();
    };
  }, [pin]);

  // True when the browser blocked a play() attempt (audience tab has
  // no prior user gesture). Flipped on by PLAY_SONG when .play()
  // rejects; cleared when actual playback starts. Surfacing this
  // shows a full-screen "Tap to play" button so any click unlocks
  // audio for the rest of the tab session.
  const [audioBlocked, setAudioBlocked] = useState(false);

  // Custom progress UI state — replaces the native <audio controls>
  // bar. Updated via the audio element's timeupdate / loadedmetadata
  // events so we render a clean progress strip + mm:ss timer instead
  // of the browser's chrome.
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioIsPlaying, setAudioIsPlaying] = useState(false);
  // True once the audio has played at least once on this slide. Lets
  // us distinguish "Ready" (never started) from "Paused" (started,
  // then paused). Reset alongside the other audio state on slide
  // change.
  const [audioHasStarted, setAudioHasStarted] = useState(false);

  const unlockAudio = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.play().then(() => setAudioBlocked(false)).catch(() => { /* user can try again */ });
  }, []);

  // Reset visible playback state whenever the audio src changes or we
  // leave the song slide entirely. Without this, the previous song's
  // "Now playing" pulse + progress fill leaked into the next slide
  // because audioIsPlaying / currentTime / duration are component
  // state (not derived from the audio element on every render).
  useEffect(() => {
    setAudioIsPlaying(false);
    setAudioCurrentTime(0);
    setAudioDuration(0);
    setAudioBlocked(false);
    setAudioHasStarted(false);
    // Also defensively pause the audio element if it still exists —
    // browsers can keep an old <audio> playing through src changes if
    // we don't tell it to stop.
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* no-op */ }
    }
  }, [slide.kind, slide.songAudioPath]);

  // Auto-scale question text. Sized in `clamp(min, vw, max)` so the same
  // slide is legible on a 1080p projector close-up AND a 4K TV viewed
  // from across a venue. Longer questions get a smaller scale to fit on
  // screen, but never drop below ~36px even on small laptops.
  // A question with choices shares the slide with the choices grid, so the
  // text is one step smaller.
  const choiceQuestionFontSize = (text: string) => {
    if (text.length > 160) return 'clamp(2rem, 3vw, 3.8rem)';
    if (text.length > 80) return 'clamp(2.4rem, 3.8vw, 4.8rem)';
    return 'clamp(3rem, 4.8vw, 6rem)';
  };

  const questionFontSize = (text: string) => {
    if (text.length > 200) return 'clamp(2.4rem, 3.6vw, 4.5rem)';
    if (text.length > 120) return 'clamp(3rem, 4.8vw, 6rem)';
    if (text.length > 60)  return 'clamp(3.8rem, 6vw, 8rem)';
    return 'clamp(4.5rem, 7.5vw, 10rem)';
  };

  // Auto-scale the round-summary list so 8 long questions (or 5 short
  // ones) both fit the viewport. Scale tracks total visual density —
  // question count × longest line length — so a small number of long
  // questions and a large number of short ones both shrink correctly.
  // The optional `scale` multiplier lets the host nudge sizes live from
  // the control panel when content edges past the auto-sizer's choice.
  const summaryFontSize = (
    questions: SummaryQuestion[],
    answersShown: boolean,
    scale: number = 1,
  ) => {
    const fmt = (lo: number, vw: number, hi: number) =>
      `clamp(${(lo * scale).toFixed(2)}rem, ${(vw * scale).toFixed(2)}vw, ${(hi * scale).toFixed(2)}rem)`;
    const count = questions.length;
    if (count === 0) return fmt(2.8, 4.2, 5);
    const longest = questions.reduce((m, q) => {
      // A multiple choice item shows its choices on a second line instead
      // of an answer after the text.
      if (q.choices && q.choices.length >= 2) {
        const line = q.choices.reduce((n, c) => n + c.length + 4, 0);
        return Math.max(m, q.text?.length ?? 0, line);
      }
      const extra = answersShown ? (q.answer?.length ?? 0) + (q.answerPartB?.length ?? 0) + 5 : 0;
      return Math.max(m, (q.text?.length ?? 0) + extra);
    }, 0);
    // A multiple choice item takes two lines.
    const lines = questions.reduce((n, q) => n + (q.choices && q.choices.length >= 2 ? 2 : 1), 0);
    const density = lines * Math.max(longest, 30);
    if (density > 900) return fmt(1.7, 2.2, 2.8);
    if (density > 560) return fmt(2.1, 2.9, 3.4);
    if (density > 320) return fmt(2.5, 3.6, 4);
    return fmt(2.8, 4.2, 5);
  };

  // Quiz-configured cap on which ranks get the prize-tier highlight.
  // Default 3 preserves the historical gold/silver/bronze treatment.
  // 0 = no highlights so a rank-1 row looks the same as the rest —
  // useful when no prizes are being awarded.
  const highlightCap = event?.quiz?.standingsHighlightCount ?? 3;
  const rankStyle = (rank: number) => {
    if (rank > highlightCap) return '';
    if (rank === 1) return 'rank-gold';
    if (rank === 2) return 'rank-silver';
    if (rank === 3) return 'rank-bronze';
    return '';
  };

  const rankLabel = (rank: number) => {
    const mod10 = rank % 10;
    const mod100 = rank % 100;
    if (mod10 === 1 && mod100 !== 11) return `${rank}st`;
    if (mod10 === 2 && mod100 !== 12) return `${rank}nd`;
    if (mod10 === 3 && mod100 !== 13) return `${rank}rd`;
    return `${rank}th`;
  };

  // Show error screen if event failed to load
  if (loadError) {
    return (
      <div className="presentation-view">
        <div className="slide slide-welcome">
          <div className="welcome-content">
            <h1 className="welcome-title" style={{ fontSize: '3rem' }}>Event Not Found</h1>
            <p className="welcome-venue">No event exists for PIN: {pin}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="presentation-view">
      {/* Connection indicator (subtle) */}
      <div className={`connection-dot ${connected ? 'connected' : 'disconnected'}`} />

      {/* Fullscreen toggle. Hidden when in fullscreen mode so it doesn't
          show on the projector. ESC exits fullscreen as normal. */}
      <FullscreenButton />

      {/* "Powered by RunMyTrivia" mark. An org with the customBranding
          plan feature can hide it in Settings → Branding. */}
      {showPoweredBy && (
        <div className="presentation-powered-by" aria-label={`Powered by ${PRODUCT_BRAND.name}`}>
          Powered by <strong>{PRODUCT_BRAND.name}</strong>
        </div>
      )}

      {/* Timer overlay */}
      {timerSeconds !== null && timerSeconds > 0 && (
        <div className="presentation-timer">{timerSeconds}</div>
      )}

      {/* Reminder banner — host-pushed message that overlays whatever
          slide is currently showing. Cleared by an empty SHOW_REMINDER
          or by CLEAR_REMINDER. */}
      {reminderText && (
        <div className="presentation-reminder" role="status">
          {reminderText}
        </div>
      )}

      {/* QR check-in code. Shown only on the pre-game slides, and only when
          the host turned check-in on for this night. */}
      {event?.checkInEnabled && pin &&
        (slide.kind === 'WELCOME' || slide.kind === 'RULES' || slide.kind === 'ROUNDS_OVERVIEW') && (
        <div className="checkin-qr-corner" aria-label="Scan to check in your team">
          <div className="outro-qr-frame">
            <QRCodeSVG
              value={`${window.location.origin}/join/${pin}`}
              size={256}
              bgColor="#ffffff"
              fgColor="#0a0a1a"
              level="M"
              marginSize={1}
            />
          </div>
          <span className="checkin-qr-label">Scan to check in your team</span>
        </div>
      )}

      {/* WELCOME / IDLE
          Headline is the event/quiz title. Prizes render as a
          clean unordered list so multi-line "$20 1st / $15 2nd / Drawing"
          configs read top-to-bottom. */}
      {slide.kind === 'WELCOME' && (() => {
        // Prefer the backend-resolved welcome content (override -> venue
        // default -> quiz). Fall back to raw quiz fields so older payloads
        // without `welcome` still render.
        const startTime = event?.welcome?.startTime ?? event?.quiz?.welcomeStartTime;
        const prizes = event?.welcome?.prizes ?? event?.quiz?.welcomePrizes;
        return (
        <div className="slide slide-welcome" key={`welcome-${animKey}`}>
          <div className="welcome-content">
            <h1 className="welcome-title">{event?.name || event?.quiz?.name || 'Tonight'}</h1>
            {(startTime || prizes) && (
              <div className="welcome-meta">
                {startTime && (
                  <div className="welcome-meta-row">
                    <span className="welcome-meta-label">Starts</span>
                    <span className="welcome-meta-value">{startTime}</span>
                  </div>
                )}
                {prizes && (
                  <div className="welcome-meta-row">
                    <span className="welcome-meta-label">Prizes</span>
                    <ul className="welcome-prizes-list">
                      {splitPrizes(prizes).map((line, i) => (
                        <li key={i} className="welcome-prizes-item">{line}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* RULES — host-rules slide. Body comes from quiz.rulesText, split
          on newlines into a bulleted list. Empty rulesText is treated as
          "no rules slide" upstream so this branch should only render
          when there's actual content. */}
      {slide.kind === 'RULES' && (
        <div className="slide slide-rules" key={`rules-${animKey}`}>
          <div className="rules-content">
            <span className="rules-overline">Tonight&apos;s</span>
            <h1 className="rules-heading">Rules</h1>
            <ul className="rules-list">
              {(event?.welcome?.rulesText ?? event?.quiz?.rulesText ?? '')
                .split(/\r?\n/)
                .map((line) => line.trim())
                .filter(Boolean)
                .map((line, i) => (
                  <li key={i} className="rules-item">{line}</li>
                ))}
            </ul>
          </div>
        </div>
      )}

      {/* ROUNDS OVERVIEW — table-of-contents for the night. Lists every
          round by number + title + type pill. Data sourced from the
          already-loaded event.quiz.rounds. */}
      {slide.kind === 'ROUNDS_OVERVIEW' && (
        <div className="slide slide-rounds-overview" key={`overview-${animKey}`}>
          <div className="overview-content">
            <h1 className="overview-heading">Tonight&apos;s Rounds</h1>
            <ol className="overview-list">
              {(event?.quiz?.rounds ?? []).map((r, i) => (
                <li key={r.id} className="overview-item">
                  <span className="overview-num">{i + 1}</span>
                  <span className="overview-title">{r.title || 'Untitled round'}</span>
                  {r.roundType && r.roundType !== 'STANDARD' && (
                    <span className={`overview-type ${r.roundType.toLowerCase()}`}>
                      {r.roundType}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {/* ROUND INTRO */}
      {slide.kind === 'ROUND_INTRO' && (
        <div className="slide slide-round-intro" key={`round-${animKey}`}>
          <div className="round-intro-content">
            <span className="round-overline">Round</span>
            <h1 className="round-number">{slide.roundNumber}</h1>
            <h2 className="round-title-text">{slide.roundTitle}</h2>
            {slide.roundType && slide.roundType !== 'STANDARD' && (
              <span className={`round-type-pill ${slide.roundType.toLowerCase()}`}>{slide.roundType} ROUND</span>
            )}
          </div>
        </div>
      )}

      {/* QUESTION */}
      {slide.kind === 'QUESTION' && (
        <div
          className={`slide slide-question ${slide.choices.length ? 'has-choices' : ''}`}
          key={`q-${animKey}`}
        >
          <div className="question-header">
            <span className="q-round-label">Round {slide.roundNumber}</span>
            <span className="q-number-label">
              Question {slide.questionNumber}{slide.totalQuestions ? ` of ${slide.totalQuestions}` : ''}
              {slide.points != null && (
                <span className="q-points-tag">{slide.points} {slide.points === 1 ? 'pt' : 'pts'}</span>
              )}
            </span>
          </div>
          <div className="question-body">
            <h1
              className="question-text"
              style={{
                fontSize: slide.choices.length
                  ? choiceQuestionFontSize(slide.questionText)
                  : questionFontSize(slide.questionText),
              }}
            >
              {slide.questionText}
            </h1>
            {slide.choices.length > 0 && <ChoiceGrid choices={slide.choices} correct={null} />}
          </div>
        </div>
      )}

      {/* ANSWER REVEAL */}
      {slide.kind === 'ANSWER' && (
        <div
          className={`slide slide-answer ${slide.choices.length && slide.correctChoice != null ? 'has-choices' : ''}`}
          key={`a-${animKey}`}
        >
          <div className="question-header">
            <span className="q-round-label">Round {slide.roundNumber}</span>
            <span className="q-number-label">Question {slide.questionNumber}</span>
          </div>
          <div className="question-body">
            <h1 className="question-text faded" style={{ fontSize: questionFontSize(slide.questionText) }}>
              {slide.questionText}
            </h1>
            {slide.choices.length > 0 && slide.correctChoice != null ? (
              // Multiple choice: the choices stay in place and the correct
              // one lights up. Without a correct index the plain answer
              // text shows instead.
              <ChoiceGrid choices={slide.choices} correct={slide.correctChoice} />
            ) : (
              <div className="answer-reveal">
                <div className="answer-wipe">
                  <h2 className="answer-value">{slide.answer}</h2>
                  {slide.answerPartB && (
                    <h3 className="answer-part-b">{slide.answerPartB}</h3>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SONG QUESTION — a song can be any question in any round. */}
      {slide.kind === 'SONG_BONUS' && (
        <div className="slide slide-song" key={`song-${animKey}`}>
          {/* Match the text-question header so the round / question
              number is visible during audio-round play. */}
          {slide.roundNumber > 0 && (
            <div className="question-header">
              <span className="q-round-label">Round {slide.roundNumber}</span>
              <span className="q-number-label">
                Question {slide.questionNumber}
                {slide.totalQuestions ? ` of ${slide.totalQuestions}` : ''}
              </span>
            </div>
          )}
          <div className="song-content">
            <h1 className="song-heading">What&apos;s the song and artist?</h1>
            {slide.songAudioPath && (
              <>
                {/* Audio element runs invisibly; the visible UI is the
                    custom progress bar below. Host drives play/pause/
                    restart via socket actions — the audience tab has
                    no buttons of its own. */}
                <audio
                  ref={audioRef}
                  preload="auto"
                  className="song-player-hidden"
                  src={
                    /^https?:\/\//i.test(slide.songAudioPath)
                      ? slide.songAudioPath
                      : `${config.apiBaseUrl}${slide.songAudioPath}`
                  }
                  onPlay={() => {
                    setAudioBlocked(false);
                    setAudioIsPlaying(true);
                    setAudioHasStarted(true);
                  }}
                  onPause={() => setAudioIsPlaying(false)}
                  onEnded={() => {
                    setAudioIsPlaying(false);
                    // Treat reaching the end as "never started" for
                    // the next play — show "Ready" instead of
                    // "Paused" so the indicator matches the rewind
                    // behavior of the Play button.
                    setAudioHasStarted(false);
                  }}
                  onTimeUpdate={(e) => setAudioCurrentTime((e.target as HTMLAudioElement).currentTime)}
                  onLoadedMetadata={(e) => {
                    const d = (e.target as HTMLAudioElement).duration;
                    if (Number.isFinite(d)) setAudioDuration(d);
                  }}
                  onError={() => {
                    if (pin) {
                      audioRef.current?.dispatchEvent(new CustomEvent('noop'));
                    }
                    window.dispatchEvent(new CustomEvent('present-audio-error', {
                      detail: { url: slide.songAudioPath },
                    }));
                  }}
                />
                <div className="song-player-ui" aria-hidden="true">
                  {(() => {
                    const variant = audioIsPlaying
                      ? 'playing'
                      : audioHasStarted
                        ? 'paused'
                        : 'idle';
                    const label = audioIsPlaying
                      ? '♪ Now playing'
                      : audioHasStarted
                        ? '⏸ Paused'
                        : 'Ready';
                    return (
                      <div className={`song-player-status ${variant}`}>{label}</div>
                    );
                  })()}
                </div>
              </>
            )}
            {audioBlocked && (
              <button
                type="button"
                onClick={unlockAudio}
                className="song-unlock-overlay"
                aria-label="Click to play audio"
              >
                <span className="song-unlock-icon">▶</span>
                <span className="song-unlock-text">Tap to play audio</span>
                <span className="song-unlock-sub">browser blocked autoplay</span>
              </button>
            )}
          </div>
          {/* Clip progress across the bottom of the slide: elapsed and
              clip length. The file's own duration wins; the quiz's
              songDuration fills in until the file reports it. */}
          {slide.songAudioPath && (() => {
            const total = audioDuration > 0 ? audioDuration : slide.songDuration;
            const pct = total > 0 ? Math.min(100, (audioCurrentTime / total) * 100) : 0;
            return (
              <div
                className="song-progress-bottom"
                role="progressbar"
                aria-label="Song clip progress"
                aria-valuemin={0}
                aria-valuemax={Math.round(total) || undefined}
                aria-valuenow={Math.round(audioCurrentTime)}
              >
                <span className="song-progress-time">{formatClock(audioCurrentTime)}</span>
                <div className="song-progress-track">
                  <div className="song-progress-fill" style={{ width: `${pct}%` }} />
                </div>
                <span className="song-progress-time">{total > 0 ? formatClock(total) : '—:—'}</span>
              </div>
            );
          })()}
        </div>
      )}

      {/* VISUAL ROUND */}
      {slide.kind === 'VISUAL_ROUND' && (
        <div className="slide slide-visual" key={`vis-${animKey}`}>
          <div className="visual-content">
            <span className="visual-overline">Visual Round</span>
            <h1 className="visual-heading">{slide.roundTitle || 'Visual Round'}</h1>
            <p className="visual-desc">Grab a visual round sheet from your host</p>
          </div>
        </div>
      )}

      {/* ROUND SUMMARY — recap of all questions in the round. Answers are
          hidden until the host clicks "Show Answers" on the host panel,
          which re-emits this slide with answersShown:true. The wrapper
          key is intentionally stable across visibleCount/answersShown
          changes so the slide doesn't remount (and replay its entrance
          animation) on every host Next click — only new <li> items
          animate in via the per-item keyframe. */}
      {slide.kind === 'ROUND_SUMMARY' && (
        <div className="slide slide-summary" key={`summary-${slide.roundNumber}`}>
          <div className="summary-content">
            <span className="summary-overline">
              Round {slide.roundNumber}{slide.roundTitle ? ` — ${slide.roundTitle}` : ''}
            </span>
            <h1 className="summary-heading">
              {slide.summaryAnswersShown ? 'Answers' : 'Round Summary'}
            </h1>
            <ol
              className="summary-questions"
              style={{
                fontSize: summaryFontSize(
                  slide.summaryQuestions,
                  slide.summaryAnswersShown,
                  slide.summaryScale,
                ),
              }}
            >
              {slide.summaryQuestions
                .slice(0, slide.summaryVisibleCount)
                .map((q, i) => (
                  <li key={i} className="summary-question-item">
                    {q.text && <span className="summary-question-text">{q.text}</span>}
                    {q.choices && q.choices.length >= 2 ? (
                      <SummaryChoices
                        choices={q.choices}
                        correct={
                          slide.summaryAnswersShown && i < slide.summaryAnswerVisibleCount
                            ? readIndex(q.correctChoice)
                            : null
                        }
                      />
                    ) : slide.summaryAnswersShown &&
                      q.answer &&
                      i < slide.summaryAnswerVisibleCount && (
                      <span className="summary-question-answer">
                        {q.text ? ' — ' : ''}<strong>{q.answer}</strong>
                        {q.answerPartB ? <em> / {q.answerPartB}</em> : null}
                      </span>
                    )}
                  </li>
                ))}
            </ol>
          </div>
        </div>
      )}

      {/* OUTRO — end-of-night slide. Shows the next handful of Brain
          Brew dates plus QR codes for the website + socials so people
          can pull out their phones while walking out. */}
      {slide.kind === 'OUTRO' && (
        <div className="slide slide-outro" key={`outro-${animKey}`}>
          <div className="outro-content">
            <div className="outro-header">
              <span className="outro-overline">Thanks for playing</span>
            </div>
            <div className="outro-schedule">
              <h2 className="outro-section-heading">Next up</h2>
              {upcoming.length === 0 ? (
                <p className="outro-empty">Check the website for our full schedule.</p>
              ) : (
                <ul className="outro-events">
                  {upcoming.map((ev) => (
                    <li key={ev.id} className="outro-event">
                      <span className="outro-event-day">
                        {ev.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/Denver' })}
                        {' · '}
                        {ev.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Denver' })}
                      </span>
                      <span className="outro-event-venue">{ev.venueName}</span>
                      {ev.theme && (
                        <span className="outro-event-theme">{ev.theme}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="outro-qrs">
              {outroLinks.map((link) => (
                <div key={link.url} className="outro-qr">
                  <div className="outro-qr-frame">
                    <QRCodeSVG
                      value={link.url}
                      size={256}
                      bgColor="#ffffff"
                      fgColor="#0a0a1a"
                      level="M"
                      marginSize={1}
                    />
                  </div>
                  <span className="outro-qr-handle">
                    <FontAwesomeIcon icon={link.icon} className="outro-qr-icon" />
                    {link.handle}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CUSTOM — a host-made slide from the slide library. */}
      {slide.kind === 'CUSTOM' && (() => {
        // A slide removed while on screen stays up until the host moves on.
        const found = event?.customSlides?.find((c) => c.id === slide.customSlideId);
        if (found) lastCustomRef.current = found;
        const custom = found
          ?? (lastCustomRef.current?.id === slide.customSlideId ? lastCustomRef.current : undefined);
        return (
          <div className={`slide slide-custom custom-${(custom?.kind ?? 'none').toLowerCase()}`} key={`custom-${animKey}`}>
            {custom && <CustomSlideContent slide={custom} venueName={event?.venue?.name ?? ''} />}
          </div>
        );
      })()}

      {/* RANDOM DRAWING — raffle for a side prize. Someone clicks Draw here,
          or the host clicks Draw in the host panel. Either side sends
          PLAY_DRAWING. The server picks the winner (rank > 2, leftEarly
          teams already excluded) and broadcasts it, so every connected
          screen runs the same animation in sync. */}
      {slide.kind === 'DRAWING' && (
        <div className="slide slide-drawing" key={`drawing-${animKey}`}>
          <div className="drawing-content">
            <span className="drawing-overline">Random Drawing</span>
            {drawPhase === 'IDLE' && (
              <>
                <h1 className="drawing-heading">Spin the wheel</h1>
                <p className="drawing-sub">
                  One winner picked from every team that&apos;s still here
                  (1st and 2nd not eligible).
                </p>
                <button
                  type="button"
                  className="drawing-button"
                  onClick={async () => {
                    setDrawError('');
                    const eventId = eventRef.current?.id;
                    if (!eventId || !socketRef.current || !pin) {
                      setDrawError('Not connected — refresh and try again.');
                      return;
                    }
                    const blocked = await drawingBlockedReason(eventId);
                    if (blocked) {
                      setDrawError(blocked);
                      return;
                    }
                    socketRef.current.emit('presentation-control', {
                      pin,
                      action: 'PLAY_DRAWING',
                      payload: {},
                    });
                  }}
                >
                  Draw Winner
                </button>
                {drawError && <p className="drawing-error">{drawError}</p>}
              </>
            )}
            {drawPhase === 'ROLLING' && (
              <>
                <h1 className="drawing-heading">Drawing…</h1>
                <div className="drawing-roller" aria-live="polite">
                  <span key={drawDisplayName} className="drawing-roller-name">
                    {drawDisplayName || ' '}
                  </span>
                </div>
              </>
            )}
            {drawPhase === 'REVEALED' && (
              <>
                <span className="drawing-winner-overline">Winner</span>
                <h1 className="drawing-winner-name">{drawWinner}</h1>
                <p className="drawing-sub">Come on up &amp; claim your prize! 🎉</p>
              </>
            )}
            {/* Confetti while winner is revealed. Burst from the
                bottom-center, scatter randomly. Each piece gets its
                own translate-X, peak height, rotation, and color via
                CSS custom properties so we get organic randomness
                without hand-writing dozens of keyframes. */}
            {drawPhase === 'REVEALED' && (
              <div className="drawing-confetti" aria-hidden="true">
                {Array.from({ length: 80 }).map((_, i) => {
                  // Seeded pseudo-randomness so the animation is
                  // identical across audience tabs (every viewer
                  // sees the same confetti pattern). The seed is the
                  // piece index, which is the same everywhere.
                  const seed = (i + 1) * 9301 + 49297;
                  const r1 = ((seed * 1.3) % 1 + 1) % 1;
                  const r2 = ((seed * 7.7) % 1 + 1) % 1;
                  const r3 = ((seed * 3.1) % 1 + 1) % 1;
                  const r4 = ((seed * 5.5) % 1 + 1) % 1;
                  // Horizontal travel: ±60vw from launch point.
                  const tx = (r1 - 0.5) * 120;
                  // Peak height before falling: 50–95vh
                  const peak = -(50 + r2 * 45);
                  // Rotation total
                  const rot = (r3 - 0.5) * 1440;
                  // Final settle y after gravity (a bit past viewport)
                  const fy = 110;
                  const delay = r4 * 0.35;
                  const dur = 2.4 + r2 * 1.2;
                  return (
                    <span
                      key={i}
                      className={`confetti-piece c-${i % 8}`}
                      style={{
                        ['--tx' as string]: `${tx}vw`,
                        ['--peak' as string]: `${peak}vh`,
                        ['--fy' as string]: `${fy}vh`,
                        ['--rot' as string]: `${rot}deg`,
                        animationDelay: `${delay.toFixed(2)}s`,
                        animationDuration: `${dur.toFixed(2)}s`,
                      } as React.CSSProperties}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* STANDINGS */}
      {slide.kind === 'STANDINGS' && (
        <div className="slide slide-standings" key={`standings-${animKey}`}>
          <div className="standings-heading-row">
            <div>
              <h1 className="standings-heading">Standings</h1>
              {standingsRound != null && event?.quiz?.rounds && standingsRound < event.quiz.rounds.length && (
                <span className="standings-as-of">After Round {standingsRound}</span>
              )}
            </div>
          </div>
          <div
            className="standings-list"
            data-density={standings.length <= 6 ? 'low' : standings.length <= 10 ? 'med' : 'high'}
          >
            {standings.length === 0 && <p className="standings-empty">No scores yet</p>}
            {/* Group consecutive entries with the same rank onto a
                single row — tied teams share a line. team names are
                joined with a middle dot; if the combined string
                overflows the row, MarqueeText scrolls horizontally. */}
            {(() => {
              type Group = { rank: number; score: number; names: string[] };
              const groups: Group[] = [];
              for (const s of standings) {
                const last = groups[groups.length - 1];
                if (last && last.rank === s.rank) {
                  last.names.push(s.teamName);
                } else {
                  groups.push({ rank: s.rank, score: s.grandTotal, names: [s.teamName] });
                }
              }
              return groups.map((g, i) => {
                const tied = g.names.length > 1;
                // Highlight the drawing winner if their name shows up
                // in this row. Trophy emoji prepended to the team name.
                const hasWinner = Boolean(
                  drawWinner && g.names.some((n) => n === drawWinner),
                );
                const displayNames = hasWinner
                  ? g.names.map((n) => (n === drawWinner ? `🏆 ${n}` : n))
                  : g.names;
                const joined = displayNames.join(' · ');
                return (
                <div
                  key={i}
                  className={`standings-row ${rankStyle(g.rank)} ${tied ? 'tied' : ''} ${hasWinner ? 'has-winner' : ''}`}
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  <span className="standings-rank">{rankLabel(g.rank)}</span>
                  <span className="standings-team">
                    <MarqueeText text={joined} />
                  </span>
                  <span className="standings-score">{g.score}</span>
                </div>
                );
              });
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// CustomSlideContent renders one host-made slide in the projector style.
// It reads only the fields of the custom slide. It has no access to
// questions, so it cannot show an answer.
// ──────────────────────────────────────────────────────────────────────
function textLines(text: string | null): string[] {
  return (text ?? '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
}

export const CustomSlideContent: React.FC<{ slide: CustomSlide; venueName: string }> = ({ slide, venueName }) => {
  const body = textLines(slide.body);

  if (slide.kind === 'IMAGE') {
    return (
      <div className="custom-content custom-image-content">
        {slide.title && <h1 className="custom-title custom-title-small">{slide.title}</h1>}
        {slide.imageUrl && <img className="custom-image" src={slide.imageUrl} alt={slide.caption || slide.title || ''} />}
        {slide.caption && <p className="custom-caption">{slide.caption}</p>}
      </div>
    );
  }

  if (slide.kind === 'PROMO') {
    return (
      <div className="custom-content custom-promo-content">
        <div className="custom-promo-text">
          {slide.title && <h1 className="custom-title">{slide.title}</h1>}
          {body.map((line, i) => <p key={i} className="custom-body-line">{line}</p>)}
        </div>
        {slide.url && (
          <div className="custom-promo-qr">
            <div className="outro-qr-frame">
              <QRCodeSVG value={slide.url} size={320} bgColor="#ffffff" fgColor="#0a0a1a" level="M" marginSize={1} />
            </div>
            {slide.caption && <span className="custom-caption">{slide.caption}</span>}
            <span className="outro-qr-handle">{slide.url.replace(/^https?:\/\/(www\.)?/, '')}</span>
          </div>
        )}
      </div>
    );
  }

  if (slide.kind === 'SCHEDULE') {
    // Expand recurring rows the same way the outro does, then keep what is
    // still ahead of us.
    const now = Date.now();
    const items: UpcomingItem[] = [];
    for (const ev of slide.schedule ?? []) {
      if (ev.type === 'recurring' && !ev.date) {
        for (const d of expandRecurring(ev)) {
          items.push({ id: `${ev.id}-${d.getTime()}`, name: ev.name, venueName, theme: ev.theme, date: d });
        }
      } else if (ev.date) {
        items.push({ id: `${ev.id}`, name: ev.name, venueName, theme: ev.theme, date: new Date(ev.date) });
      }
    }
    const upcoming = items
      .filter((i) => i.date.getTime() > now)
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .slice(0, 6);
    return (
      <div className="custom-content custom-schedule-content">
        <span className="rules-overline">{venueName}</span>
        <h1 className="custom-title">{slide.title || 'Next week'}</h1>
        {upcoming.length === 0 ? (
          <p className="outro-empty">Check the website for our full schedule.</p>
        ) : (
          <ul className="outro-events">
            {upcoming.map((ev) => (
              <li key={ev.id} className="outro-event">
                <span className="outro-event-day">
                  {ev.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/Denver' })}
                  {' · '}
                  {ev.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Denver' })}
                </span>
                <span className="outro-event-venue">{ev.name}</span>
                {ev.theme && <span className="outro-event-theme">{ev.theme}</span>}
              </li>
            ))}
          </ul>
        )}
        {body.map((line, i) => <p key={i} className="custom-body-line">{line}</p>)}
      </div>
    );
  }

  // TEXT: a headline and a few lines, in the rules-slide voice.
  return (
    <div className="custom-content custom-text-content">
      {slide.title && <h1 className="custom-title">{slide.title}</h1>}
      {body.map((line, i) => <p key={i} className="custom-body-line">{line}</p>)}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// MarqueeText — renders text statically when it fits the parent, and
// horizontally scrolls (looping) when it doesn't. Used to squeeze tied
// teams onto a single standings row without truncation.
// ──────────────────────────────────────────────────────────────────────
const MarqueeText: React.FC<{ text: string }> = ({ text }) => {
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const contentRef = useRef<HTMLSpanElement | null>(null);
  const [overflow, setOverflow] = useState(false);

  useEffect(() => {
    const measure = () => {
      const w = wrapperRef.current;
      const c = contentRef.current;
      if (!w || !c) return;
      // Compare the natural content width vs the visible row width.
      // Add 1 px slack to avoid flapping on subpixel rounding.
      setOverflow(c.scrollWidth > w.clientWidth + 1);
    };
    measure();
    // Re-measure on resize so 1080p TVs (and dev resizes) stay correct.
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [text]);

  if (!overflow) {
    return (
      <span ref={wrapperRef} className="marquee-static">
        <span ref={contentRef}>{text}</span>
      </span>
    );
  }
  return (
    <span ref={wrapperRef} className="marquee-wrapper">
      <span className="marquee-track">
        <span ref={contentRef} className="marquee-content">{text}</span>
        {/* Duplicate so the loop is seamless — when the first copy
            scrolls off-screen the second is in position to take over. */}
        <span aria-hidden="true" className="marquee-content">{text}</span>
      </span>
    </span>
  );
};

export default PresentationView;
